import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, money, fmtDate, fmtHours, API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import RichTextEditorField from "../components/RichTextEditor";
import RichTextView from "../components/RichTextView";
import SummaryCard from "../components/SummaryCard";
import { toast } from "sonner";
import {
  Plus, FileDown, Filter, Eye, Pencil, Copy, ThumbsUp, ThumbsDown, Mail, Trash2, AlertTriangle,
  MessageCircle, Search, ClipboardList, CheckCircle2, Clock, AlertCircle, ChevronLeft, ChevronRight,
  MoreHorizontal, Download, X, FileSignature, Building2, Settings, IdCard, Calendar, User, ListChecks, ListTodo,
  ArrowUp, ArrowDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useDirty } from "../hooks/useDirty";
import ConfirmCloseDialog from "../components/ConfirmCloseDialog";
import { UNIDADE_HORA, UNIDADE_DIA, unidadeDe, fmtDias } from "../lib/unidadeCobranca";

// Redesenho da listagem (delta 2026-09-16, referência visual fornecida) —
// só a LISTAGEM muda (cabeçalho, cards de resumo, busca/filtros,
// tabela, paginação). Formulário de Nova/Editar ficha e os demais
// dialogs (visualizar/rejeitar/assinatura/excluir) permanecem
// exatamente como já homologados — mesmos handlers, mesmas permissões,
// mesmos endpoints.
const PAGE_SIZE_OPCOES = [10, 25, 50];
// Status que já existem no backend (mesmo domínio de statusPt/statusColor
// abaixo) mas não cabem nos 4 cards da referência visual — "Pendentes/Em
// edição" agrupa tudo que ainda não chegou em faturamento/cancelamento
// (nunca uma métrica inventada: é só uma soma de contagens reais).
const STATUS_FATURADA = "faturada";
const STATUS_AGUARDANDO_FATURAMENTO = "aguardando_faturamento";
const STATUS_FINALIZADOS_OU_FORA_DO_FLUXO = new Set([STATUS_FATURADA, STATUS_AGUARDANDO_FATURAMENTO, "cancelada"]);
// "A concluir" (item 5/7 do pedido) — reaproveita o status "em_andamento"
// já existente (ficha criada/salva mas ainda não enviada para
// aprovação/assinatura, ver TicketIn.encerrar em server.py) em vez de
// inventar um status novo. Excluído aqui de "Pendentes/Em edição" (abaixo)
// para virar seu próprio card, sem contar duas vezes.
const STATUS_A_CONCLUIR = "em_andamento";
// Cards clicáveis (delta 2026-09-16, referência visual fornecida) — mesmo
// predicado usado para CONTAR "Pendentes/Em edição" nos cards (stats,
// abaixo) e para FILTRAR a listagem quando esse card é clicado — uma
// única definição, nunca duas implementações do mesmo agrupamento.
const isPendenteEmEdicao = (t) => t.status !== STATUS_A_CONCLUIR && !STATUS_FINALIZADOS_OU_FORA_DO_FLUXO.has(t.status);

// Revisão final pré-produção (delta 2026-09-16) — mapa único cardKey ->
// predicado de status, usado tanto para CONTAR cada card (stats) quanto
// para FILTRAR a tabela quando ele está ativo (filteredItems, mais
// abaixo) — nunca duas implementações do mesmo agrupamento por card.
const CARD_STATUS_PREDICATES = {
  faturadas: (t) => t.status === STATUS_FATURADA,
  aguardando_faturamento: (t) => t.status === STATUS_AGUARDANDO_FATURAMENTO,
  pendentes: isPendenteEmEdicao,
  a_concluir: (t) => t.status === STATUS_A_CONCLUIR,
};

// SummaryCard (ícone+título compacto, número em destaque, percentual na
// base, sem seta/chevron/lápis) — extraído para components/SummaryCard.jsx
// (delta 2026-09-16, referência visual fornecida) para que Faturamento &
// Cobrança use a MESMA linguagem visual, uma única implementação.

const statusPt = (s) => ({
  em_andamento: "Em andamento", concluida: "Concluída",
  rejeitada_supervisor: "Rejeitada pelo supervisor", rejeitada_cliente: "Rejeitada pelo cliente",
  aguardando_aprovacao: "Aguardando aprovação",
  aguardando_assinatura: "Aguardando assinatura",
  aguardando_faturamento: "Aguardando faturamento", faturada: "Faturada", cancelada: "Cancelada",
}[s] || s);
const statusColor = (s) => ({
  em_andamento: "bg-blue-100 text-blue-800", concluida: "bg-emerald-100 text-emerald-800",
  rejeitada_supervisor: "bg-red-100 text-red-800", rejeitada_cliente: "bg-red-100 text-red-800",
  aguardando_aprovacao: "bg-purple-100 text-purple-800",
  aguardando_assinatura: "bg-cyan-100 text-cyan-800",
  aguardando_faturamento: "bg-indigo-100 text-indigo-800",
  faturada: "bg-slate-200 text-slate-800", cancelada: "bg-red-100 text-red-800",
}[s] || "bg-slate-100 text-slate-700");

// Cabeçalho de coluna ordenável (delta 2026-09-18) — indicador ↑/↓
// discreto só na coluna ativa; "Ações" nunca recebe isto (não é
// sortable).
function SortableTh({ label, columnKey, sortKey, sortDir, onSort, className = "" }) {
  const active = sortKey === columnKey;
  return (
    <th className={className}>
      <button
        type="button" onClick={() => onSort(columnKey)}
        className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900"
        data-testid={`ficha-sort-${columnKey}`}
      >
        {label}
        {active && (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </button>
    </th>
  );
}

// Banner de rejeição (supervisor ou cliente) — exibido dentro da ficha (edição e
// visualização) para dar visibilidade a quem rejeitou e o motivo, com a rejeição
// atual em destaque e as anteriores em "Histórico de rejeições". Os campos "ao
// vivo" do supervisor (rejection_reason/rejected_by_name/rejected_at) e do cliente
// (signer_rejected_*) só refletem a rejeição corrente; approval_rejection_history
// e signature_rejection_history guardam TODAS as rejeições já sofridas pela ficha
// — inclusive as que já foram corrigidas/reaprovadas/reassinadas — e continuam
// consultáveis mesmo depois que um novo token de assinatura é emitido (o que
// arquiva e limpa os campos "ao vivo" da rejeição do cliente).
function RejectionHistory({ items }) {
  if (!items.length) return null;
  return (
    <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="font-medium text-slate-700 mb-2">Histórico de rejeições</div>
      <div className="space-y-2">
        {items.map((h, i) => (
          <div key={i} className="border-t border-slate-200 first:border-t-0 pt-2 first:pt-0">
            <div className="text-xs text-slate-500">
              <b>{h.origin === "supervisor" ? "Supervisor" : "Cliente"}</b>
              {h.name && <> — {h.name}</>}
              {h.at && <> · {new Date(h.at).toLocaleString("pt-BR")}</>}
            </div>
            {h.reason && <div className="text-slate-700 mt-0.5">{h.reason}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function RejectionNotice({ t }) {
  if (!t) return null;

  const approvalHistory = t.approval_rejection_history || [];
  // Enquanto a rejeição do supervisor ainda está "ao vivo" (status ainda é
  // rejeitada_supervisor), o último item do array É a rejeição em destaque —
  // não repete no histórico. Depois de corrigida/reaprovada, todos os itens
  // já são passados.
  const pastApproval = (t.status === "rejeitada_supervisor" ? approvalHistory.slice(0, -1) : approvalHistory)
    .map(h => ({ origin: "supervisor", name: h.rejected_by_name || h.rejected_by, at: h.rejected_at, reason: h.reason }));
  // signature_rejection_history só recebe uma entrada quando o token é
  // superado por um novo — ou seja, nunca contém a rejeição "ao vivo" atual.
  const pastSignature = (t.signature_rejection_history || [])
    .map(h => ({ origin: "cliente", name: h.rejected_by, at: h.rejected_at, reason: h.reason }));
  const pastRejections = [...pastApproval, ...pastSignature]
    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));

  let current = null;
  if (t.status === "rejeitada_supervisor") {
    current = (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
        <b>✗ Rejeitada pelo supervisor</b>
        {t.rejected_by_name && <> — por <b>{t.rejected_by_name}</b></>}
        {t.rejected_at && <> em {new Date(t.rejected_at).toLocaleString("pt-BR")}</>}
        {t.rejection_reason && <div className="mt-1">Motivo: {t.rejection_reason}</div>}
      </div>
    );
  } else if (t.status === "rejeitada_cliente") {
    current = (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
        <b>✗ Rejeitada pelo cliente</b>
        {t.signer_rejected_by && <> — por <b>{t.signer_rejected_by}</b></>}
        {t.signer_rejected_at && <> em {new Date(t.signer_rejected_at).toLocaleString("pt-BR")}</>}
        {t.signer_rejected_reason && <div className="mt-1">Motivo: {t.signer_rejected_reason}</div>}
      </div>
    );
  }

  if (!current && pastRejections.length === 0) return null;
  return (
    <div className="space-y-2">
      {current}
      <RejectionHistory items={pastRejections} />
    </div>
  );
}

// Visualização interna pelo ícone "olho" (delta 2026-09-16, referência
// visual fornecida) — mesma organização/hierarquia do PDF já homologado
// da Ficha (server.py: _build_ticket_pdf_response/_pdf_field_table), só
// como HTML interativo: Cliente/Atendimento/Resumo de horas e
// valores/Descrição/Pendências/Próximos passos/Encerramento. Dados
// sempre os já calculados pelo backend — nenhum campo novo inventado.
function ViewField({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-800 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
function ViewInfoItem({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate">{value ?? "—"}</div>
      </div>
    </div>
  );
}
function ViewMetricCard({ label, value, accent }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${accent ? "border-l-2 border-l-primary" : ""}`}>
      <div className="text-[10px] font-semibold tracking-wide text-slate-500 uppercase">{label}</div>
      <div className="text-lg font-heading font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
function ViewYesNoBadge({ value }) {
  if (value === null || value === undefined) return <Badge className="bg-slate-100 text-slate-600">Não informado</Badge>;
  return value
    ? <Badge className="bg-emerald-100 text-emerald-800">Sim</Badge>
    : <Badge className="bg-red-100 text-red-800">Não</Badge>;
}
const SISTEMA_FUNCIONANDO_LABELS = { sim: "Sim", nao: "Não", na: "Não se aplica" };

// Espelha a regra do backend (NON_EDITABLE_TICKET_STATUSES em server.py):
// ficha assinada pelo cliente é imutável, e os status abaixo só mudam via
// rotas de negócio dedicadas (aprovação, assinatura, faturamento, cancelamento).
const NON_EDITABLE_STATUSES = new Set([
  "aguardando_aprovacao", "aguardando_assinatura", "aguardando_faturamento", "faturada", "cancelada",
]);
const canEditTicket = (t) => !t.signed_at && !NON_EDITABLE_STATUSES.has(t.status);

// Espelha TICKET_DELETE_STATUSES_ANALISTA / _SUPERVISOR_ADMIN em server.py — só
// controla a exibição do botão; a autorização real é sempre revalidada no
// backend (POST /tickets/{id}/delete), então divergir aqui não abre brecha,
// só esconde/mostra o ícone incorretamente na tela.
const TICKET_DELETE_STATUSES_ANALISTA = new Set([
  "em_andamento", "rejeitada_supervisor", "rejeitada_cliente", "aguardando_aprovacao", "aguardando_assinatura",
]);
const TICKET_DELETE_STATUSES_SUPERVISOR_ADMIN = new Set([...TICKET_DELETE_STATUSES_ANALISTA, "aguardando_faturamento"]);
const canDeleteTicket = (t, user) => {
  if (!t || !user || t.deleted || t.status === "faturada") return false;
  if (user.role === "analista") return t.analista_id === user.id && TICKET_DELETE_STATUSES_ANALISTA.has(t.status);
  if (user.role === "supervisor") return t.supervisor_id === user.id && TICKET_DELETE_STATUSES_SUPERVISOR_ADMIN.has(t.status);
  if (user.role === "admin") return TICKET_DELETE_STATUSES_SUPERVISOR_ADMIN.has(t.status);
  return false;
};
const DELETE_CONFIRM_PHRASE = "CONFIRMO A EXCLUSÃO";

// Pacote 2, item 7 — Tipo de contato estruturado: obrigatório em fichas
// NOVAS (ver validação no submit() e em create_ticket no backend); fichas
// antigas sem este campo continuam sendo exibidas/editadas normalmente.
const TIPO_CONTATO_OPTIONS = ["WhatsApp", "Celular", "Telefone", "E-mail", "Outros"];

// Autopreenchimento do Contato a partir do cadastro do Cliente (delta
// 2026-09-19) — mapeamento contra o schema REAL de Cliente (ClientIn,
// server.py): só os 3 tipos com campo correspondente direto.
// "Celular"/"Outros" NUNCA preenchem sozinhos (não existe client.celular/
// client.outros) — o campo `contato` do CLIENTE também não entra aqui:
// é "Pessoa de contato" (nome de uma pessoa, ver Clientes.jsx), não um
// canal de comunicação (telefone/e-mail), então não é um valor válido
// para o campo Contato da Ficha (que espera um WhatsApp/telefone/e-mail).
const CONTATO_CLIENTE_FIELD_MAP = { WhatsApp: "whatsapp", Telefone: "telefone", "E-mail": "email" };
export function contatoFromCliente(clientes, clienteId, tipoContato) {
  const campo = CONTATO_CLIENTE_FIELD_MAP[tipoContato];
  if (!campo || !clienteId) return "";
  const cliente = clientes.find(c => c.id === clienteId);
  return cliente?.[campo] || "";
}

// Recalcula sempre que `contato` bate com o autopreenchimento do
// Cliente/Tipo ANTERIOR (nunca deixa o contato do cliente antigo
// "vazar" para o novo); preserva quando o valor é uma digitação manual.
export function nextContatoAoTrocar(clientes, form, { novoClienteId = form.cliente_id, novoTipoContato = form.tipo_contato } = {}) {
  const valorAutoAnterior = contatoFromCliente(clientes, form.cliente_id, form.tipo_contato);
  const eraAutoPreenchido = !form.contato || form.contato === valorAutoAnterior;
  if (!eraAutoPreenchido) return form.contato;
  return contatoFromCliente(clientes, novoClienteId, novoTipoContato);
}

const empty = {
  cliente_id: "", contato: "", tipo_contato: "", analista_id: "", supervisor_id: "",
  produto: "",
  // Pacote 2, item 5 — referência ao catálogo (products.id), preenchida só
  // quando o usuário seleciona um item do <Select> (nunca digitada). Ver
  // handler do Select de Produto/Sistema mais abaixo.
  produto_id: null,
  tipo_servico: "Suporte", data_servico: new Date().toISOString().slice(0, 10),
  // Pacote pós-homologação, item 1: Início/Término nascem vazios (antes
  // vinham pré-preenchidos com 09:00/12:00); Intervalo e Horas abonadas já
  // nasciam em 0 antes desta mudança e continuam assim — nada a alterar
  // aqui. Fichas já existentes não são afetadas (isto só é o estado inicial
  // do formulário de UMA NOVA ficha). Valor-hora: comportamento já
  // homologado preservado — quando fica null (padrão), o backend usa o
  // valor_hora cadastrado no cliente (ver compute_ticket em server.py); só
  // é sobrescrito se o usuário digitar um valor específico aqui.
  hora_inicio: "", hora_fim: "", intervalo_min: 0, horas_abonadas: 0,
  valor_hora: null,
  // valor_total só é ENVIADO quando valor_origem === "total" (ver submit).
  valor_total: null, valor_origem: "unidade",
  // Forma de cobrança + campos da diária (usados só quando "dia").
  unidade_cobranca: "hora", quantidade_dias: null, valor_dia: null,
  descricao: "", pendencias: "", proximos_passos: "",
  objetivo_cumprido: true, motivo_nao_cumprido: "", tem_pendencia: false,
  sistema_funcionando: "sim", encerrar: false, status: "em_andamento",
};

// FORMA DE COBRANÇA da ficha: por HORA (padrão/histórico) ou por DIA.
// A unidade define APENAS como o valor_total é calculado — faturamento,
// parcelamento, PIX, boleto e misto continuam usando só o valor_total.
// Os helpers da unidade vivem em lib/unidadeCobranca.js — compartilhados
// com as listagens do Faturamento, para a MESMA ficha nunca aparecer com
// rótulos diferentes em telas diferentes. Reexportados aqui porque os
// testes desta tela já os importam daqui.
export { UNIDADE_HORA, UNIDADE_DIA, unidadeDe, fmtDias };

// A ORIGEM do cálculo é sempre explícita, nunca inferida, justamente para
// não existir o laço unitário -> total -> unitário:
//   "unidade" (padrão): total = quantidade x valor unitário
//                       (horas faturáveis x valor/hora, ou dias x diária)
//   "total"  (o usuário digitou o total): o valor FECHADO manda e o
//            unitário é derivado — mudar a quantidade depois NÃO altera o
//            total, só recalcula o unitário equivalente.
// A origem só volta para "unidade" quando o usuário edita o campo do valor
// unitário. O BACKEND repete esta conta (compute_ticket) e decide de fato.
export function computeLocal(f, cli) {
  const [h1, m1] = (f.hora_inicio || "0:0").split(":").map(Number);
  const [h2, m2] = (f.hora_fim || "0:0").split(":").map(Number);
  const diff = Math.max(0, (h2 * 60 + m2) - (h1 * 60 + m1) - (Number(f.intervalo_min) || 0));
  const hRealizadas = +(diff / 60).toFixed(2);
  const hFat = Math.max(0, +(hRealizadas - (Number(f.horas_abonadas) || 0)).toFixed(2));
  const porDia = unidadeDe(f) === UNIDADE_DIA;
  // Quantidade e valor unitário da unidade vigente.
  const qtd = porDia ? Math.max(0, Number(f.quantidade_dias) || 0) : hFat;

  if (f.valor_origem === "total") {
    const total = Math.max(0, Number(f.valor_total) || 0);
    const unit = qtd > 0 ? +(total / qtd).toFixed(2) : 0;
    return { hRealizadas, hFat, qtd, unit, total, porDia, vh: porDia ? 0 : unit };
  }
  const informado = porDia ? f.valor_dia : f.valor_hora;
  const unit = informado != null && informado !== ""
    ? Number(informado)
    : (porDia ? 0 : Number(cli?.valor_hora || 0));
  const total = +(qtd * unit).toFixed(2);
  return { hRealizadas, hFat, qtd, unit, total, porDia, vh: porDia ? 0 : unit };
}

export default function Fichas() {
  const { user, hasFeature, isProduction } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  // Nome do tenant para o cabeçalho da visualização interna (item 6, "Nome
  // da empresa") — mesmo dado já usado no PDF da ficha (company.razao_social/
  // nome_fantasia); nunca inventado, só reaproveitado de /config/company
  // (já usado em Configuracoes.jsx com o mesmo endpoint).
  const [company, setCompany] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(null);
  const [signLink, setSignLink] = useState(null);
  // filters = os mesmos filtros de sempre, agora incluindo "tipo_servico"
  // (backend já aceita esse parâmetro em GET /tickets — só não estava
  // exposto no frontend; nenhum endpoint novo). "q" (nº exato da ficha)
  // saiu daqui: a nova busca principal (searchText, abaixo) cobre nº da
  // ficha por SUBSTRING sobre os dados já carregados, um superconjunto do
  // que o backend já fazia.
  const [filters, setFilters] = useState({ cliente_id: "", analista_id: "", status: "", tipo_servico: "", start: "", end: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Card de resumo ativo (delta 2026-09-16, item 9) — só bookkeeping
  // visual de QUAL card está "selecionado"; a filtragem em si continua
  // usando os mesmos filtros/dados de sempre (backend `filters.status`
  // para os cards de status único, filtragem client-side sobre `items`
  // já carregado para "Pendentes/Em edição", que é um AGRUPAMENTO de
  // vários status — nunca representável no parâmetro `status` único do
  // backend, então nunca precisou de endpoint/campo novo).
  const [activeCard, setActiveCard] = useState(null); // null | "faturadas" | "aguardando_faturamento" | "pendentes" | "a_concluir"
  // Busca principal (item 3) — client-side sobre a lista já carregada por
  // /tickets (mesmo endpoint, mesmos filtros de backend já aplicados);
  // nunca refeita a cada tecla no servidor.
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPCOES[0]);
  // Ordenação da listagem (delta 2026-09-18) — abre por Nº da ficha DESC
  // (maior primeiro), nunca por cliente. Cabeçalhos clicáveis alternam
  // ASC/DESC; trocar de coluna sempre começa em ASC.
  const [sortKey, setSortKey] = useState("numero");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  // Integração WhatsApp (Fase 1 — Fundação) — preview do destinatário (item
  // 10) e envio/reenvio do link já existente por esse canal (item 8). O
  // backend resolve/valida o número; aqui só se exibe o que ele devolveu.
  const [whatsappRecipient, setWhatsappRecipient] = useState(null);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappSentOnce, setWhatsappSentOnce] = useState(false);

  // `overrideFilters` (opcional) evita o problema clássico de closure
  // "stale state" do React — `limparFiltros` abaixo precisa recarregar
  // com os filtros JÁ vazios, não com o valor de `filters` capturado
  // antes do `setFilters` (que só se aplica no próximo render).
  const load = async (overrideFilters) => {
    try {
      const params = Object.fromEntries(Object.entries(overrideFilters || filters).filter(([, v]) => v));
      const { data } = await api.get("/tickets", { params });
      setItems(data);
    } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
  };

  const limparFiltros = () => {
    const cleared = { cliente_id: "", analista_id: "", status: "", tipo_servico: "", start: "", end: "" };
    setFilters(cleared);
    setSearchText("");
    setActiveCard(null); // item 9 — "Limpar filtros" também desativa o destaque do card, coerente com o status limpo
    load(cleared);
  };

  // Correção de regressão pré-produção (delta 2026-09-16) — "A concluir"/
  // Faturadas/Aguardando faturamento antes gravavam `filters.status` e
  // recarregavam `items` já FILTRADO pelo backend só para aquele status.
  // Como `items` também é a fonte de `stats` (useMemo, mais abaixo), isso
  // corrompia a contagem dos OUTROS cards a cada clique (ex.: clicar em
  // "Faturadas" zerava "Aguardando faturamento"/"Pendentes"/"A concluir"
  // e igualava "Total de fichas" ao valor de "Faturadas" — todos
  // calculados sobre o MESMO `items`, agora truncado). Corrigido para o
  // MESMO padrão já usado (e sempre correto) por "Pendentes/Em edição":
  // nunca restringe `items` por status no backend — só marca
  // `activeCard`; o filtro real acontece client-side em `filteredItems`
  // (CARD_STATUS_PREDICATES, acima), sobre o universo COMPLETO já
  // carregado. `items`/`stats` continuam representando o MESMO universo
  // coerente não importa qual card esteja ativo. `filters.status` é
  // sempre limpo (nunca setado por um card) para nunca deixar um filtro
  // de status "travado" de um clique anterior.
  const filtrarPorStatusCard = (cardKey) => {
    const novos = { ...filters, status: "" };
    setFilters(novos);
    setActiveCard(cardKey);
    load(novos);
  };
  const filtrarAConcluir = () => filtrarPorStatusCard("a_concluir");

  // "Total de fichas" — remove só o filtro de STATUS aplicado pelos cards
  // (preserva cliente/analista/tipo/período que o usuário tenha definido
  // na seção Filtros, à parte dos cards).
  const mostrarTodasFichas = () => {
    const novos = { ...filters, status: "" };
    setFilters(novos);
    setActiveCard(null);
    load(novos);
  };

  // "Pendentes / Em edição" — é um AGRUPAMENTO de vários status
  // (isPendenteEmEdicao, CARD_STATUS_PREDICATES.pendentes acima), nunca
  // representável no parâmetro `status` único que GET /tickets aceita —
  // mesmo mecanismo client-side de filtrarPorStatusCard serve também
  // para este caso, sem nenhuma diferença de implementação.
  const filtrarPendentesEmEdicao = () => filtrarPorStatusCard("pendentes");

  useEffect(() => {
    (async () => {
      try {
        const [c, u, s, p, g, comp] = await Promise.all([
          api.get("/clients"),
          user.role !== "analista" ? api.get("/users").catch(() => ({ data: [] })) : api.get("/users", { params: { active_only: true } }).catch(() => ({ data: [] })),
          api.get("/config/service-types"),
          api.get("/config/products").catch(() => ({ data: [] })),
          api.get("/config/groups").catch(() => ({ data: [] })),
          api.get("/config/company").catch(() => ({ data: {} })),
        ]);
        setClients(c.data.filter(x => x.active !== false)); setUsers(u.data); setTypes(s.data);
        setProducts(p.data); setGroups(g.data); setCompany(comp.data || {});
      } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
    })();
    load();
    // eslint-disable-next-line
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, analista_id: user.role === "analista" ? user.id : "" });
    setOpen(true);
  };
  const openEdit = (t) => {
    if (!canEditTicket(t)) {
      toast.error(t.signed_at ? "Ficha já assinada pelo cliente não pode ser editada" : "Ficha neste status não pode ser editada diretamente");
      return;
    }
    setEditing(t); setForm({ ...empty, ...t }); setOpen(true);
  };
  const openView = async (t) => { const { data } = await api.get(`/tickets/${t.id}`); setView(data); };

  const closeForm = () => { setOpen(false); setForm(empty); setEditing(null); };
  // Proteção contra perda de preenchimento (item 4/13) — "sujo" só depois
  // que o usuário de fato altera algo em relação ao que a ficha tinha ao
  // abrir (useDirty compara contra a fotografia capturada na abertura).
  const dirty = useDirty(open, form);
  const [confirmCloseFichaOpen, setConfirmCloseFichaOpen] = useState(false);
  const requestCloseForm = () => { if (dirty) setConfirmCloseFichaOpen(true); else closeForm(); };

  // Trocar a FORMA DE COBRANÇA zera o lado financeiro da outra unidade e
  // devolve a origem para "unidade" — nunca carrega um valor fechado que foi
  // digitado pensando na outra base de cálculo.
  const setUnidade = (unidade) => setForm(f => ({
    ...f, unidade_cobranca: unidade, valor_origem: "unidade", valor_total: null,
    ...(unidade === UNIDADE_DIA ? { valor_hora: null } : { quantidade_dias: null, valor_dia: null }),
  }));

  // Mudar as HORAS/DIAS NÃO muda a origem do cálculo. Com a origem em "total", o
  // valor fechado digitado é o que vale: mexer no horário só recalcula o
  // valor/hora equivalente (1.000 em 8h = 125/h; as mesmas 1.000 em 10h =
  // 100/h, e o total segue 1.000). A origem só volta para "hora" quando o
  // usuário edita o campo Valor hora.
  const setHoras = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async (overrides = {}) => {
    if (!form.hora_inicio || !form.hora_fim) {
      return toast.error("Informe o horário de início e o horário de término do atendimento.");
    }
    // Pacote 2, item 7 — obrigatório só em fichas NOVAS (mesma regra do
    // backend em create_ticket); editar uma ficha antiga sem este campo
    // preenchido continua funcionando normalmente. "A concluir" (item 5/6)
    // NÃO dispensa isto — são campos estruturalmente exigidos pelo
    // backend para qualquer ficha, não uma regra do fluxo de aprovação.
    if (!editing && !form.tipo_contato) {
      return toast.error("Selecione o tipo de contato.");
    }
    try {
      const payload = { ...form, ...overrides };
      // Contrato de origem única (ver computeLocal): o backend só recebe
      // `valor_total` quando foi ELE o último campo editado; caso contrário
      // o cálculo lá é o histórico (horas faturáveis x valor/hora).
      delete payload.valor_origem;
      if (form.valor_origem !== "total" || payload.valor_total == null) delete payload.valor_total;
      if (payload.valor_hora === "" || payload.valor_hora == null) delete payload.valor_hora;
      if (editing) await api.put(`/tickets/${editing.id}`, payload);
      else await api.post("/tickets", payload);
      toast.success(overrides.encerrar === false ? "Ficha salva como \"A concluir\"" : "Ficha salva");
      closeForm(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dl = (id, numero) => {
    const url = `${API_BASE}/tickets/${id}/pdf`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = `ficha_${numero}.pdf`; a.click();
      }).catch(() => toast.error("Falha ao gerar PDF"));
  };

  const requestSignature = async (t) => {
    try {
      const { data } = await api.post(`/tickets/${t.id}/signature-request`);
      setSignLink({ ...data, ticket: t });
      setWhatsappRecipient(null);
      setWhatsappSentOnce(false);
      // Planos/Recursos (item 15) — sem o recurso "whatsapp_integration"
      // contratado pela empresa, nem tenta consultar o destinatário: o
      // backend bloquearia com 403 mesmo assim, mas evita a chamada
      // desnecessária e mantém o card de envio simplesmente ausente.
      // Delta — ocultar envio integrado em produção: mesma lógica, nunca
      // busca o preview (nem o card aparece) fora de homolog — evita uma
      // chamada de rede inútil e qualquer ponto visual remanescente do
      // envio integrado quando o botão em si já está oculto.
      if (hasFeature("whatsapp_integration") && !isProduction) {
        api.get(`/tickets/${t.id}/whatsapp/recipient-preview`).then(r => setWhatsappRecipient(r.data)).catch(() => setWhatsappRecipient(null));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resendSignature = async () => {
    if (!signLink?.ticket) return;
    try {
      const { data } = await api.post(`/tickets/${signLink.ticket.id}/signature-resend`);
      // update link with new token (regenerated)
      const upd = await api.post(`/tickets/${signLink.ticket.id}/signature-request`);
      setSignLink({ ...upd.data, ticket: signLink.ticket, resent: data.sent });
      if (data.sent) toast.success("E-mail reenviado ao cliente"); else toast.info("Link regenerado (cliente sem e-mail cadastrado)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Integração WhatsApp (Fase 1) — mesmo link já existente, agora também
  // disparável por esse canal. "Enviar" (primeira vez) e "Reenviar" chamam
  // rotas distintas por causa da auditoria (item 14: enviado × reenviado),
  // mas o comportamento no frontend é o mesmo: mostrar o resultado e
  // atualizar a lista.
  const sendWhatsapp = async (resend) => {
    if (!signLink?.ticket) return;
    setWhatsappSending(true);
    try {
      const { data } = await api.post(`/tickets/${signLink.ticket.id}/whatsapp/${resend ? "resend-signature-link" : "send-signature-link"}`);
      if (data.sent) { toast.success("Link enviado por WhatsApp"); setWhatsappSentOnce(true); }
      else toast.error(data.error || "Não foi possível enviar por WhatsApp agora");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWhatsappSending(false); }
  };

  // Correções pós-homologação, itens 20-24 — WhatsApp gratuito (wa.me),
  // TOTALMENTE separado da integração automática Gold (whatsapp_integration)
  // acima: nunca checa hasFeature, sempre disponível. A LOOTUS só abre a
  // conversa com a mensagem pronta — o usuário confirma o envio manualmente
  // no WhatsApp/WhatsApp Web, por isso nunca registramos "enviado" aqui.
  const [whatsappShareOpening, setWhatsappShareOpening] = useState(false);
  const shareWhatsappFree = async () => {
    if (!signLink?.ticket) return;
    setWhatsappShareOpening(true);
    try {
      const { data } = await api.post(`/tickets/${signLink.ticket.id}/whatsapp/share-link`);
      window.open(data.wa_link, "_blank", "noopener,noreferrer");
      toast.success("WhatsApp aberto com a mensagem pronta — confirme o envio por lá");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWhatsappShareOpening(false); }
  };

  const copyLink = () => {
    if (signLink?.url) {
      navigator.clipboard.writeText(signLink.url);
      toast.success("Link copiado");
    }
  };

  const approve = async (t) => {
    try { await api.post(`/tickets/${t.id}/approve`); toast.success("Ficha aprovada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const rejectApproval = async () => {
    if (!rejectReason.trim()) return toast.error("Informe o motivo");
    try {
      await api.post(`/tickets/${rejectOpen.id}/reject-approval`, { reason: rejectReason });
      toast.success("Ficha devolvida para o analista");
      setRejectOpen(null); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [deleteOpen, setDeleteOpen] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const openDelete = (t) => { setDeleteOpen(t); setDeleteReason(""); setDeleteConfirmText(""); };
  const deleteReasonOk = deleteReason.trim().length > 0;
  const deleteConfirmOk = deleteConfirmText === DELETE_CONFIRM_PHRASE;
  const confirmDelete = async () => {
    if (!deleteReasonOk || !deleteConfirmOk || !deleteOpen) return;
    setDeleting(true);
    try {
      await api.post(`/tickets/${deleteOpen.id}/delete`, { reason: deleteReason, confirm: deleteConfirmText });
      toast.success("Ficha excluída");
      setDeleteOpen(null); setDeleteReason(""); setDeleteConfirmText(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const cli = clients.find(c => c.id === form.cliente_id);
  const calc = computeLocal(form, cli);

  const analistasList = users.filter(u => u.role === "analista" && u.active !== false);
  const supervisoresList = users.filter(u => u.role === "supervisor" && u.active !== false);

  // Pacote 2, item 4 — Produto/Sistema organizado por Grupo no seletor da
  // Ficha. Produtos sem Grupo (cadastrados no Pacote 1, antes de Grupos
  // existir) ficam agrupados à parte, nunca escondidos.
  const activeProducts = products.filter(p => p.active !== false);
  const groupById = Object.fromEntries(groups.map(g => [g.id, g]));
  const productsByGroup = {};
  const productsNoGroup = [];
  activeProducts.forEach(p => {
    if (p.group_id && groupById[p.group_id]) {
      (productsByGroup[p.group_id] ||= []).push(p);
    } else {
      productsNoGroup.push(p);
    }
  });

  // Item 2 — cards de resumo, calculados sobre os dados REAIS já
  // carregados (`items` = resultado atual de /tickets, já considerando os
  // filtros de backend aplicados). Nunca uma comparação com período
  // anterior/crescimento — o backend não guarda esse histórico.
  const stats = useMemo(() => {
    const total = items.length;
    const faturadas = items.filter(t => t.status === STATUS_FATURADA).length;
    const aguardandoFaturamento = items.filter(t => t.status === STATUS_AGUARDANDO_FATURAMENTO).length;
    // "A concluir" sai de "Pendentes/Em edição" para ter seu próprio card
    // (item 7) — mesma origem de dados (items), nunca contado duas vezes.
    const aConcluir = items.filter(t => t.status === STATUS_A_CONCLUIR).length;
    const pendentes = items.filter(isPendenteEmEdicao).length;
    const pct = (n) => (total ? Math.round((n / total) * 1000) / 10 : 0);
    return {
      total, faturadas, aguardandoFaturamento, aConcluir, pendentes,
      pctFaturadas: pct(faturadas), pctAguardando: pct(aguardandoFaturamento), pctAConcluir: pct(aConcluir), pctPendentes: pct(pendentes),
    };
  }, [items]);

  // Item 3 — busca principal (nº da ficha, cliente, serviço, colaborador),
  // client-side sobre o resultado já carregado (mesmos dados usados na
  // tabela hoje — nenhum refetch por tecla digitada).
  const filteredItems = useMemo(() => {
    // Todo card ativo filtra aqui, client-side, pelo MESMO predicado usado
    // para contá-lo em `stats` (CARD_STATUS_PREDICATES, topo do arquivo) —
    // `items` nunca é restrito por status no backend por causa de um card
    // (ver filtrarPorStatusCard), então `stats`/os demais cards nunca ficam
    // incoerentes entre si depois de um clique.
    const predicate = activeCard && CARD_STATUS_PREDICATES[activeCard];
    const base = predicate ? items.filter(predicate) : items;
    const term = searchText.trim().toLowerCase();
    if (!term) return base;
    return base.filter(t => {
      const clienteNome = clients.find(c => c.id === t.cliente_id)?.razao_social || "";
      const analistaNome = users.find(u => u.id === t.analista_id)?.name || "";
      const alvo = [t.numero, clienteNome, t.tipo_servico, analistaNome].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(term);
    });
  }, [items, searchText, clients, users, activeCard]);

  // Ordenação (delta 2026-09-18) — sempre DEPOIS dos filtros e ANTES da
  // paginação (filtros → ordenação → paginação), sobre os mesmos dados já
  // carregados/filtrados (nenhum refetch por ordenação). Tipo de
  // comparação por coluna: ficha (numérico), data (cronológico — string
  // ISO "AAAA-MM-DD" já ordena cronologicamente por texto), cliente/
  // serviço/status (alfabético, localeCompare pt-BR), horas/valor
  // (numérico).
  const SORTABLE_COLUMNS = useMemo(() => ({
    numero: { type: "numeric", get: (t) => Number(t.numero) || 0 },
    data_servico: { type: "text", get: (t) => t.data_servico || "" },
    cliente: { type: "text", get: (t) => clients.find(c => c.id === t.cliente_id)?.razao_social || "" },
    tipo_servico: { type: "text", get: (t) => t.tipo_servico || "" },
    horas_realizadas: { type: "numeric", get: (t) => t.horas_realizadas || 0 },
    valor_total: { type: "numeric", get: (t) => t.valor_total || 0 },
    status: { type: "text", get: (t) => statusPt(t.status) || "" },
  }), [clients]);

  const sortedItems = useMemo(() => {
    const col = SORTABLE_COLUMNS[sortKey];
    if (!col) return filteredItems;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredItems].sort((a, b) => {
      const va = col.get(a), vb = col.get(b);
      if (col.type === "numeric") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "pt-BR", { sensitivity: "base" }) * dir;
    });
  }, [filteredItems, sortKey, sortDir, SORTABLE_COLUMNS]);

  useEffect(() => { setPage(1); }, [searchText, pageSize, items, sortKey, sortDir]);
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));
  const pageAtual = Math.min(page, totalPages);
  const pageItems = sortedItems.slice((pageAtual - 1) * pageSize, pageAtual * pageSize);
  const filtrosAtivos = Object.values(filters).some(Boolean);

  // Item 7 — "Exportar": só viável aqui 100% no frontend (CSV a partir dos
  // dados já carregados/filtrados, sem chamada nova ao backend). Escapa
  // cada campo (aspas duplicadas + campo entre aspas) — nunca concatena
  // texto do usuário cru numa string de download.
  const exportarCsv = () => {
    const linhas = [["Nº", "Data", "Cliente", "Serviço", "Horas", "Valor", "Status"]];
    filteredItems.forEach(t => {
      const clienteNome = clients.find(c => c.id === t.cliente_id)?.razao_social || "";
      linhas.push([t.numero, fmtDate(t.data_servico), clienteNome, t.tipo_servico, fmtHours(t.horas_realizadas), money(t.valor_total), statusPt(t.status)]);
    });
    const csv = linhas.map(l => l.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "fichas_de_servico.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Fichas de Serviço</h1>
          <p className="text-slate-500 text-sm">Registro completo dos atendimentos</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={openNew} data-testid="ficha-nova"><Plus size={16} /> Nova ficha</Button>
      </div>

      {/* Item 2/7 — cards de resumo, calculados sobre os dados reais já
          carregados; agora todos clicáveis como filtro rápido (delta
          2026-09-16, referência visual fornecida). */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={ClipboardList} label="Total de fichas" value={stats.total} pct={null} color="blue"
          active={activeCard === null} onClick={mostrarTodasFichas} testId="ficha-card-total"
        />
        <SummaryCard
          icon={CheckCircle2} label="Faturadas" value={stats.faturadas} pct={stats.pctFaturadas} color="emerald"
          active={activeCard === "faturadas"} onClick={() => filtrarPorStatusCard("faturadas")}
          testId="ficha-card-faturadas"
        />
        <SummaryCard
          icon={Clock} label="Aguardando faturamento" value={stats.aguardandoFaturamento} pct={stats.pctAguardando} color="amber"
          active={activeCard === "aguardando_faturamento"}
          onClick={() => filtrarPorStatusCard("aguardando_faturamento")}
          testId="ficha-card-aguardando-faturamento"
        />
        <SummaryCard
          icon={AlertCircle} label="Pendentes / Em edição" value={stats.pendentes} pct={stats.pctPendentes} color="red"
          active={activeCard === "pendentes"} onClick={filtrarPendentesEmEdicao} testId="ficha-card-pendentes"
        />
        <SummaryCard
          icon={ListTodo} label="A concluir" value={stats.aConcluir} pct={stats.pctAConcluir} color="purple"
          active={activeCard === "a_concluir"}
          onClick={filtrarAConcluir}
          testId="ficha-card-a-concluir"
        />
      </div>

      {/* Item 3 — busca principal + filtros (os mesmos de sempre, reorganizados) */}
      <Card className="p-4 rounded-2xl shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              className="pl-8" value={searchText} onChange={e => setSearchText(e.target.value)}
              placeholder="Buscar por nº da ficha, cliente, serviço, colaborador..." data-testid="ficha-search"
            />
          </div>
          <Button variant={filtrosAtivos ? "default" : "outline"} onClick={() => setFiltersOpen(v => !v)} data-testid="ficha-filtros-toggle">
            <Filter size={16} /> Filtros{filtrosAtivos ? " •" : ""}
          </Button>
          <Button variant="outline" onClick={exportarCsv} disabled={filteredItems.length === 0} data-testid="ficha-exportar">
            <Download size={16} /> Exportar
          </Button>
        </div>
        {filtersOpen && (
          <div className="pt-2 border-t border-slate-100 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Cliente</Label>
                <Select value={filters.cliente_id || "all"} onValueChange={v => setFilters({ ...filters, cliente_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-cliente"><SelectValue placeholder="Todos os clientes" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos os clientes</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Serviço</Label>
                <Select value={filters.tipo_servico || "all"} onValueChange={v => setFilters({ ...filters, tipo_servico: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-tipo-servico"><SelectValue placeholder="Todos os serviços" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos os serviços</SelectItem>{types.map(t => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filters.status || "all"} onValueChange={v => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="em_andamento">Em andamento</SelectItem>
                    <SelectItem value="rejeitada_supervisor">Rejeitada pelo supervisor</SelectItem>
                    <SelectItem value="rejeitada_cliente">Rejeitada pelo cliente</SelectItem>
                    <SelectItem value="aguardando_aprovacao">Aguardando aprovação</SelectItem>
                    <SelectItem value="aguardando_assinatura">Aguardando assinatura</SelectItem>
                    <SelectItem value="aguardando_faturamento">Aguardando faturamento</SelectItem>
                    <SelectItem value="faturada">Faturada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {user.role !== "analista" && (
                <div>
                  <Label className="text-xs">Analista</Label>
                  <Select value={filters.analista_id || "all"} onValueChange={v => setFilters({ ...filters, analista_id: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-analista"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todos</SelectItem>{analistasList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Período — de</Label>
                <Input type="date" value={filters.start} onChange={e => setFilters({ ...filters, start: e.target.value })} data-testid="filter-start" />
              </div>
              <div>
                <Label className="text-xs">Período — até</Label>
                <Input type="date" value={filters.end} onChange={e => setFilters({ ...filters, end: e.target.value })} data-testid="filter-end" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={limparFiltros} data-testid="filter-clear"><X size={16} /> Limpar filtros</Button>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => load()} data-testid="filter-apply">
                <Search size={16} /> Filtrar
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Item 4 — tabela reorganizada; item 5 — ações padronizadas (slots de
          largura consistente, "..." só quando há outras ações disponíveis) */}
      <Card className="p-0 overflow-hidden rounded-2xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <SortableTh label="Nº da ficha" columnKey="numero" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="py-3 px-4" />
                <SortableTh label="Data" columnKey="data_servico" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Cliente" columnKey="cliente" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Serviço" columnKey="tipo_servico" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Horas" columnKey="horas_realizadas" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Valor" columnKey="valor_total" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(t => {
                const cl = clients.find(c => c.id === t.cliente_id);
                const podeAprovar = t.status === "aguardando_aprovacao" && user.role !== "analista";
                const podeExcluir = canDeleteTicket(t, user);
                const temMenu = podeAprovar || podeExcluir;
                return (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">
                      <button
                        type="button" className="text-primary hover:underline font-semibold" onClick={() => openView(t)}
                        data-testid={`ficha-view-${t.numero}`} title="Visualizar ficha"
                      >
                        {t.numero}
                      </button>
                    </td>
                    <td>{fmtDate(t.data_servico)}</td>
                    <td>{cl?.razao_social || "-"}</td>
                    <td>{t.tipo_servico}</td>
                    <td>{fmtHours(t.horas_realizadas)}</td>
                    <td>{money(t.valor_total)}</td>
                    <td><Badge className={statusColor(t.status)}>{statusPt(t.status)}</Badge></td>
                    <td className="text-right pr-4">
                      <div className="flex items-center justify-end gap-0.5">
                        <span className="w-8 flex justify-center">
                          <Button size="sm" variant="ghost" onClick={() => openView(t)} title="Visualizar" data-testid={`ficha-view-btn-${t.numero}`}><Eye size={16} /></Button>
                        </span>
                        <span className="w-8 flex justify-center">
                          <Button size="sm" variant="ghost" onClick={() => dl(t.id, t.numero)} title="Baixar PDF" data-testid={`ficha-pdf-${t.numero}`}><FileDown size={16} /></Button>
                        </span>
                        <span className="w-8 flex justify-center">
                          <Button size="sm" variant="ghost" onClick={() => requestSignature(t)} data-testid={`ficha-sign-${t.numero}`} title={t.signed_at ? "Assinada" : "Solicitar assinatura"}>
                            <FileSignature size={16} className={t.signed_at ? "text-emerald-600" : t.signer_rejected_at ? "text-red-600" : ""} />
                          </Button>
                        </span>
                        <span className="w-8 flex justify-center">
                          {canEditTicket(t) && <Button size="sm" variant="ghost" onClick={() => openEdit(t)} title="Editar" data-testid={`ficha-edit-${t.numero}`}><Pencil size={16} /></Button>}
                        </span>
                        <span className="w-8 flex justify-center">
                          {temMenu && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" title="Mais ações" data-testid={`ficha-menu-${t.numero}`}><MoreHorizontal size={16} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {podeAprovar && (
                                  <>
                                    <DropdownMenuItem onClick={() => approve(t)} data-testid={`ficha-approve-${t.numero}`}>
                                      <ThumbsUp size={14} className="mr-2 text-emerald-600" /> Aprovar
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setRejectOpen(t); setRejectReason(""); }} data-testid={`ficha-reject-${t.numero}`}>
                                      <ThumbsDown size={14} className="mr-2 text-red-600" /> Rejeitar
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {podeExcluir && (
                                  <DropdownMenuItem onClick={() => openDelete(t)} data-testid={`ficha-delete-${t.numero}`} className="text-red-600 focus:text-red-600">
                                    <Trash2 size={14} className="mr-2" /> Excluir ficha
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400">
                  {items.length === 0 ? "Nenhuma ficha" : "Nenhuma ficha encontrada para os filtros atuais"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Item 6 — paginação (client-side sobre os dados já carregados; GET /tickets inalterado) */}
        {filteredItems.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span>Itens por página</span>
              <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                <SelectTrigger className="w-20 h-8" data-testid="ficha-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPCOES.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span data-testid="ficha-page-info">
                Mostrando {(pageAtual - 1) * pageSize + 1} a {Math.min(pageAtual * pageSize, filteredItems.length)} de {filteredItems.length} ficha{filteredItems.length === 1 ? "" : "s"}
              </span>
              <Button size="sm" variant="outline" disabled={pageAtual <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="ficha-page-prev">
                <ChevronLeft size={16} />
              </Button>
              <Button size="sm" variant="outline" disabled={pageAtual >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="ficha-page-next">
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Form dialog — item 8: tela cheia (praticamente toda a área útil no
          desktop), colunas mais largas para reduzir altura/rolagem; scroll
          continua disponível como fallback em telas pequenas (nunca
          esconde campo para "caber"). Item 4: backdrop não-dismissable,
          ESC/X/Cancelar com alteração pendem confirmação (onOpenChange
          nunca fecha direto — sempre passa por requestCloseForm). */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) requestCloseForm(); }}>
        <DialogContent
          className="w-[97vw] max-w-[1500px] h-[92vh] max-h-[92vh] flex flex-col"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => { e.preventDefault(); requestCloseForm(); }}
        >
          <DialogHeader className="shrink-0"><DialogTitle>{editing ? `Editar Ficha #${editing.numero}` : "Nova ficha de serviço"}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 space-y-4">
            <RejectionNotice t={editing} />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <Label>Cliente *</Label>
                <Select
                  value={form.cliente_id}
                  onValueChange={v => setForm({ ...form, cliente_id: v, contato: nextContatoAoTrocar(clients, form, { novoClienteId: v }) })}
                >
                  <SelectTrigger data-testid="form-cliente"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.data_servico} onChange={e => setForm({ ...form, data_servico: e.target.value })} /></div>
              <div>
                <Label>Tipo de contato{!editing && " *"}</Label>
                <Select
                  value={form.tipo_contato || "none"}
                  onValueChange={v => {
                    const tipo = v === "none" ? "" : v;
                    setForm({ ...form, tipo_contato: tipo, contato: nextContatoAoTrocar(clients, form, { novoTipoContato: tipo }) });
                  }}
                >
                  <SelectTrigger data-testid="form-tipo-contato"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {!editing ? null : <SelectItem value="none">—</SelectItem>}
                    {TIPO_CONTATO_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Contato</Label><Input placeholder="Ex.: (27) 99999-9999" value={form.contato} onChange={e => setForm({ ...form, contato: e.target.value })} data-testid="form-contato" /></div>
              {user.role !== "analista" ? (
                <div>
                  {/* Analista OPCIONAL para quem conduz a ficha — Admin
                      (2026-09-19) e, a partir deste delta, também
                      Supervisor: ele podia criar a ficha mas qualquer
                      gravação (inclusive "A concluir") travava pedindo um
                      Analista. Mesma regra validada de verdade no backend
                      (create_ticket/update_ticket); isto aqui é só a UI
                      deixar de forçar uma escolha. Nunca preenche sozinho
                      com o próprio usuário — `openNew` já garante isso. */}
                  <Label>Analista (opcional)</Label>
                  <Select
                    value={form.analista_id || "none"}
                    onValueChange={v => setForm({ ...form, analista_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="form-analista"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {analistasList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : <div><Label>Analista</Label><Input disabled value={user.name} /></div>}
              <div>
                <Label>Supervisor</Label>
                <Select value={form.supervisor_id || "none"} onValueChange={v => setForm({ ...form, supervisor_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{supervisoresList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de serviço</Label>
                <Select value={form.tipo_servico} onValueChange={v => setForm({ ...form, tipo_servico: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map(t => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Produto/Sistema</Label>
                <Select
                  value={form.produto_id || (form.produto ? "legacy" : "none")}
                  onValueChange={v => {
                    if (v === "none") return setForm({ ...form, produto: "", produto_id: null });
                    if (v === "legacy") return; // valor legado — nada a mudar, é o próprio form.produto atual
                    const p = activeProducts.find(pr => pr.id === v);
                    setForm({ ...form, produto: p?.nome || "", produto_id: v });
                  }}
                >
                  <SelectTrigger data-testid="form-produto"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {Object.entries(productsByGroup).map(([gid, items]) => (
                      <SelectGroup key={gid}>
                        <SelectLabel>{groupById[gid].nome}</SelectLabel>
                        {items.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectGroup>
                    ))}
                    {productsNoGroup.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Sem grupo</SelectLabel>
                        {productsNoGroup.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectGroup>
                    )}
                    {/* Ficha antiga com um valor de produto digitado livremente (sem
                        produto_id) — preserva o valor legado em vez de apagá-lo
                        silenciosamente ao abrir a ficha para edição. */}
                    {form.produto && !form.produto_id && (
                      <SelectItem value="legacy">{form.produto} (valor legado)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* FORMA DE COBRANÇA — define apenas COMO o valor total da
                ficha é calculado. Horário/intervalo/abono continuam
                existindo nas duas formas (registro operacional do
                atendimento); na diária eles não entram na conta do
                dinheiro. Nenhuma permissão nova: quem edita a ficha
                escolhe a forma. */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <Label>Forma de cobrança</Label>
                <Select value={unidadeDe(form)} onValueChange={setUnidade}>
                  <SelectTrigger data-testid="form-unidade-cobranca"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNIDADE_HORA}>Por hora</SelectItem>
                    <SelectItem value={UNIDADE_DIA}>Por dia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Início</Label><Input type="time" value={form.hora_inicio} onChange={e => setHoras({ hora_inicio: e.target.value })} data-testid="form-inicio" /></div>
              <div><Label>Término</Label><Input type="time" value={form.hora_fim} onChange={e => setHoras({ hora_fim: e.target.value })} data-testid="form-fim" /></div>
              <div><Label>Intervalo (min)</Label><Input type="number" value={form.intervalo_min} onChange={e => setHoras({ intervalo_min: parseInt(e.target.value || 0) })} /></div>
              <div><Label>Horas abonadas</Label><Input type="number" step="0.5" value={form.horas_abonadas} onChange={e => setHoras({ horas_abonadas: parseFloat(e.target.value || 0) })} /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {calc.porDia && (
                <div>
                  <Label>Quantidade de dias</Label>
                  <Input
                    type="number" step="0.5" min="0"
                    value={form.quantidade_dias ?? ""}
                    onChange={e => setHoras({ quantidade_dias: e.target.value === "" ? null : parseFloat(e.target.value) })}
                    data-testid="form-quantidade-dias"
                  />
                </div>
              )}
              <div>
                <Label>{calc.porDia ? "Valor da diária (R$)" : "Valor hora (R$)"}</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.valor_origem === "total"
                    ? (calc.unit || "")
                    : ((calc.porDia ? form.valor_dia : form.valor_hora) ?? "")}
                  placeholder={!calc.porDia && cli ? String(cli.valor_hora) : ""}
                  onChange={e => {
                    const v = e.target.value === "" ? null : parseFloat(e.target.value);
                    setForm(f => ({
                      ...f, valor_origem: "unidade",
                      ...(calc.porDia ? { valor_dia: v } : { valor_hora: v }),
                    }));
                  }}
                  data-testid={calc.porDia ? "form-valor-dia" : "form-valor-hora"}
                />
              </div>
              <div>
                <Label>Valor total (R$)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.valor_origem === "total" ? (form.valor_total ?? "") : (calc.total || "")}
                  onChange={e => setForm(f => ({
                    ...f, valor_origem: "total",
                    valor_total: e.target.value === "" ? null : parseFloat(e.target.value),
                  }))}
                  data-testid="form-valor-total"
                />
              </div>
            </div>
            {form.valor_origem === "total" && calc.qtd <= 0 && Number(form.valor_total) > 0 && (
              <p className="text-xs text-red-600" data-testid="form-valor-total-erro">
                {calc.porDia
                  ? "Para informar um valor total é preciso ter quantidade de dias maior que zero."
                  : "Para informar um valor total é preciso ter horas faturáveis maiores que zero. Ajuste o horário, o intervalo ou as horas abonadas."}
              </p>
            )}

            <Card className="p-3 bg-blue-50 border-blue-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="calc-resumo">
                {calc.porDia ? (
                  <>
                    <div><div className="text-xs text-slate-500">Dias trabalhados</div><div className="font-heading font-bold text-lg" data-testid="calc-dias">{fmtDias(calc.qtd)}</div></div>
                    <div><div className="text-xs text-slate-500">Valor da diária</div><div className="font-heading font-bold text-lg" data-testid="calc-unit">{money(calc.unit)}</div></div>
                  </>
                ) : (
                  <>
                    <div><div className="text-xs text-slate-500">Horas realizadas</div><div className="font-heading font-bold text-lg">{fmtHours(calc.hRealizadas)}</div></div>
                    <div><div className="text-xs text-slate-500">Horas faturáveis</div><div className="font-heading font-bold text-lg">{fmtHours(calc.hFat)}</div></div>
                    <div><div className="text-xs text-slate-500">Valor hora</div><div className="font-heading font-bold text-lg" data-testid="calc-unit">{money(calc.unit)}</div></div>
                  </>
                )}
                <div><div className="text-xs text-slate-500">Valor total</div><div className="font-heading font-bold text-lg text-primary" data-testid="calc-total">{money(calc.total)}</div></div>
              </div>
            </Card>

            <div>
              <Label>Descrição / Histórico do serviço</Label>
              <RichTextEditorField value={form.descricao} onChange={html => setForm(f => ({ ...f, descricao: html }))} testId="form-descricao" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Pendências</Label><Textarea rows={3} value={form.pendencias} onChange={e => setForm({ ...form, pendencias: e.target.value })} /></div>
              <div><Label>Próximos passos</Label><Textarea rows={3} value={form.proximos_passos} onChange={e => setForm({ ...form, proximos_passos: e.target.value })} /></div>
            </div>

            <Card className="p-3">
              <div className="font-heading font-semibold mb-2">Encerramento</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label>Objetivo cumprido?</Label>
                  <Select value={String(form.objetivo_cumprido)} onValueChange={v => setForm({ ...form, objetivo_cumprido: v === "true" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Sim</SelectItem><SelectItem value="false">Não</SelectItem></SelectContent>
                  </Select>
                </div>
                {!form.objetivo_cumprido && (
                  <div><Label>Motivo</Label><Input value={form.motivo_nao_cumprido} onChange={e => setForm({ ...form, motivo_nao_cumprido: e.target.value })} /></div>
                )}
                <div>
                  <Label>Ficou pendência?</Label>
                  <Select value={String(form.tem_pendencia)} onValueChange={v => setForm({ ...form, tem_pendencia: v === "true" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Sim</SelectItem><SelectItem value="false">Não</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sistema funcionando?</Label>
                  <Select value={form.sistema_funcionando} onValueChange={v => setForm({ ...form, sistema_funcionando: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sim">Sim</SelectItem>
                      <SelectItem value="nao">Não</SelectItem>
                      <SelectItem value="na">Não se aplica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.encerrar} onChange={e => setForm({ ...form, encerrar: e.target.checked })} data-testid="form-encerrar" />
                    <span className="text-sm">Encerrar atendimento (envia para aprovação/assinatura do cliente)</span>
                  </label>
                </div>
              </div>
            </Card>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={requestCloseForm} data-testid="form-cancelar-ficha">Cancelar</Button>
            {/* Item 5/6 — "A concluir" salva tudo já preenchido, fecha a
                ficha e a mantém disponível para retomar depois, SEMPRE com
                encerrar=false (independente do checkbox acima) — nunca
                envia para aprovação/assinatura/faturamento. */}
            <Button variant="outline" onClick={() => submit({ encerrar: false })} data-testid="form-a-concluir-ficha">A concluir</Button>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => submit()} data-testid="form-salvar-ficha">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmCloseDialog
        open={confirmCloseFichaOpen} onOpenChange={setConfirmCloseFichaOpen}
        onConfirm={() => { setConfirmCloseFichaOpen(false); closeForm(); }}
        title="Descartar ficha?"
        description="Você preencheu dados desta ficha e ainda não salvou. Se sair agora, o preenchimento será perdido — use &quot;A concluir&quot; para salvar sem finalizar."
      />

      {/* View — visualização interna pelo ícone "olho" (item 6): mesma
          organização do PDF já homologado da ficha (Cliente/Atendimento/
          Resumo de horas e valores/Descrição/Pendências/Próximos passos/
          Encerramento), como HTML interativo dentro do fluxo autenticado
          normal (isolamento por tenant/permissão inalterado — os dados
          vêm do MESMO GET /tickets/{id} de sempre). */}
      <Dialog open={!!view} onOpenChange={v => !v && setView(null)}>
        <DialogContent className="max-w-3xl">
          {view && (() => {
            const cli = clients.find(c => c.id === view.cliente_id);
            const analista = users.find(u => u.id === view.analista_id);
            const contatoDisplay = view.contato
              ? `${view.contato}${view.tipo_contato ? ` (${view.tipo_contato})` : ""}`
              : "Não informado";
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-start justify-between gap-3 flex-wrap pr-6">
                    <div>
                      <div className="font-heading font-bold text-slate-900">{company.nome_fantasia || company.razao_social || "Empresa"}</div>
                      <div className="text-xs font-semibold tracking-wide text-primary mt-0.5">RELATÓRIO DE ATENDIMENTO</div>
                      <div className="text-xs font-normal text-slate-500 mt-0.5">Ficha nº {view.numero} • {fmtDate(view.data_servico)}</div>
                    </div>
                    <Badge className={statusColor(view.status)}>{statusPt(view.status)}</Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                  <RejectionNotice t={view} />

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-3 pb-3 border-b border-slate-100">
                    <ViewInfoItem Icon={Building2} label="Cliente" value={cli?.razao_social} />
                    <ViewInfoItem Icon={IdCard} label="CPF/CNPJ" value={cli?.cpf_cnpj} />
                    <ViewInfoItem Icon={Calendar} label="Data" value={fmtDate(view.data_servico)} />
                    <ViewInfoItem Icon={Settings} label="Serviço" value={view.tipo_servico} />
                    <ViewInfoItem Icon={User} label="Analista" value={analista?.name} />
                    <ViewInfoItem Icon={Clock} label="Horário" value={`${view.hora_inicio} – ${view.hora_fim}`} />
                    <ViewInfoItem Icon={Clock} label="Horas" value={fmtHours(view.horas_realizadas)} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5"><Building2 size={14} /> Cliente</div>
                      <div className="space-y-2">
                        <ViewField label="Nome/Razão social" value={cli?.razao_social} />
                        <ViewField label="CPF/CNPJ" value={cli?.cpf_cnpj} />
                        <ViewField label="Contato" value={contatoDisplay} />
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5"><Settings size={14} /> Atendimento</div>
                      <div className="space-y-2">
                        <ViewField label="Analista" value={analista?.name} />
                        <ViewField label="Tipo de serviço" value={view.tipo_servico} />
                        <ViewField label="Produto/Sistema" value={view.produto || "Não informado"} />
                        <ViewField label="Data" value={fmtDate(view.data_servico)} />
                        <ViewField label="Horário" value={`${view.hora_inicio} às ${view.hora_fim}`} />
                        <ViewField label="Intervalo" value={`${view.intervalo_min || 0} min`} />
                      </div>
                    </div>
                  </div>

                  {/* Resumo reflete a FORMA DE COBRANÇA da ficha. Ficha
                      legada (sem unidade_cobranca) é lida como "hora", então
                      continua exibida exatamente como sempre. */}
                  {unidadeDe(view) === UNIDADE_DIA ? (
                    <div data-testid="ficha-view-resumo-dia">
                      <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5"><Clock size={14} /> Resumo de diárias e valores</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <ViewMetricCard label="Quantidade de dias" value={fmtDias(view.quantidade_dias)} />
                        <ViewMetricCard label="Valor da diária" value={money(view.valor_dia)} />
                        <ViewMetricCard label="Valor total" value={money(view.valor_total)} accent />
                      </div>
                    </div>
                  ) : (
                  <div data-testid="ficha-view-resumo-hora">
                    <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5"><Clock size={14} /> Resumo de horas e valores</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <ViewMetricCard label="Horas realizadas" value={fmtHours(view.horas_realizadas)} />
                      <ViewMetricCard label="Horas abonadas" value={fmtHours(view.horas_abonadas)} />
                      <ViewMetricCard label="Horas faturáveis" value={fmtHours(view.horas_faturaveis)} />
                      <ViewMetricCard label="Valor total" value={money(view.valor_total)} accent />
                    </div>
                    {view.valor_hora != null && <div className="text-xs text-slate-500 mt-1.5">Valor da hora: {money(view.valor_hora)}</div>}
                  </div>
                  )}

                  <div>
                    <div className="text-xs font-semibold text-primary mb-1">Descrição / Histórico do Serviço</div>
                    <RichTextView html={view.descricao} className="text-sm text-slate-700" emptyText="Nenhuma descrição registrada." />
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-semibold text-primary mb-1 flex items-center gap-1.5"><AlertTriangle size={14} /> Pendências</div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{view.pendencias || "Nenhuma pendência registrada."}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-primary mb-1 flex items-center gap-1.5"><ListChecks size={14} /> Próximos passos</div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{view.proximos_passos || "Nenhum próximo passo registrado."}</div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5"><CheckCircle2 size={14} /> Encerramento</div>
                    {/* Delta visual — resposta (pill) colada na pergunta, não
                        mais espalhada pelas pontas da coluna (justify-between
                        esticava rótulo/badge pra extremidades opostas). */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2" data-testid="ficha-view-encerramento-objetivo"><span className="text-sm text-slate-600">Objetivo cumprido?</span><ViewYesNoBadge value={view.objetivo_cumprido} /></div>
                      <div className="flex items-center gap-2" data-testid="ficha-view-encerramento-pendencia"><span className="text-sm text-slate-600">Ficou pendência?</span><ViewYesNoBadge value={view.tem_pendencia} /></div>
                      <div className="flex items-center gap-2" data-testid="ficha-view-encerramento-sistema"><span className="text-sm text-slate-600">Sistema funcionando?</span><Badge className="bg-slate-100 text-slate-700">{SISTEMA_FUNCIONANDO_LABELS[view.sistema_funcionando] || "Não informado"}</Badge></div>
                    </div>
                    {view.objetivo_cumprido === false && view.motivo_nao_cumprido && (
                      <div className="text-xs text-slate-600 mt-2"><b>Motivo:</b> {view.motivo_nao_cumprido}</div>
                    )}
                  </div>

                  {view.signed_at && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2" data-testid="ficha-view-assinatura">
                      Assinado por <b>{view.signer_name}</b> em {new Date(view.signed_at).toLocaleString("pt-BR")}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
          <DialogFooter><Button variant="outline" onClick={() => view && dl(view.id, view.numero)}><FileDown size={16} /> PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reject approval dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={v => !v && setRejectOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ThumbsDown className="text-red-600" size={18} /> Rejeitar aprovação — Ficha #{rejectOpen?.numero}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">A ficha voltará para o analista com o motivo abaixo.</p>
            <div><Label>Motivo</Label><Textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={rejectApproval} data-testid="confirm-reject-approval">Rejeitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature link dialog */}
      <Dialog open={!!signLink} onOpenChange={v => !v && setSignLink(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature size={18} className="text-primary" /> Link de assinatura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Envie o link abaixo para o cliente. Ele poderá abrir em qualquer navegador (celular/desktop), revisar os dados da ficha e assinar digitalmente.
              Após a assinatura, ela será incluída no PDF da ficha.
            </p>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-md break-all font-mono text-xs" data-testid="sign-link-url">{signLink?.url}</div>
            {signLink?.ticket?.signed_at && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">✓ Esta ficha já foi assinada por <b>{signLink.ticket.signer_name}</b></div>}
            {signLink?.ticket?.signer_rejected_at && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">✗ Rejeitada por <b>{signLink.ticket.signer_rejected_by}</b>: {signLink.ticket.signer_rejected_reason}</div>}
            {signLink?.ticket?.signature_email_sent_at && !signLink?.ticket?.signed_at && <div className="text-xs text-slate-500">E-mail já enviado em {new Date(signLink.ticket.signature_email_sent_at).toLocaleString("pt-BR")}</div>}

            {/* Integração WhatsApp (Fase 1) — item 10: mostra para qual
                número o link seria enviado e a origem, em linguagem
                amigável (nunca "service_ticket"/"client_registration"),
                antes do envio. A decisão final é sempre revalidada no
                backend no momento do clique (item 9.4). */}
            {!signLink?.ticket?.signed_at && whatsappRecipient && (
              <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="whatsapp-recipient-preview">
                <div className="flex items-center gap-2 font-medium text-emerald-800"><MessageCircle size={16} /> Enviar link por WhatsApp</div>
                {whatsappRecipient.status === "ok" ? (
                  <>
                    <div className="text-xs text-slate-600 mt-1">Destinatário: <b>{whatsappRecipient.destinatario}</b></div>
                    <div className="text-xs text-slate-500">Origem: {whatsappRecipient.origem_label}</div>
                  </>
                ) : (
                  <div className="text-xs text-amber-700 mt-1">
                    {whatsappRecipient.status === "invalid"
                      ? `O WhatsApp encontrado (${whatsappRecipient.origem_label}) está em um formato inválido.`
                      : "Nenhum WhatsApp encontrado na ficha ou no cadastro do cliente."}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setSignLink(null)}>Fechar</Button>
            {!signLink?.ticket?.signed_at && <Button variant="outline" onClick={resendSignature} data-testid="resend-sign"><Mail size={16} /> Reenviar por e-mail</Button>}
            {/* Delta — ocultar envio INTEGRADO por WhatsApp em produção
                (depende da integração automática, ainda não pronta para
                uso real): só oculta este botão — "Compartilhar pelo
                WhatsApp" (wa.me manual, abaixo), e-mail e "Copiar link"
                continuam intocados em qualquer ambiente. Homolog continua
                mostrando normalmente para testes. */}
            {!signLink?.ticket?.signed_at && !isProduction && (
              <Button
                variant="outline" onClick={() => sendWhatsapp(whatsappSentOnce)}
                disabled={whatsappSending || whatsappRecipient?.status !== "ok"}
                title={whatsappRecipient?.status !== "ok" ? "Nenhum WhatsApp válido disponível" : undefined}
                data-testid="send-whatsapp"
              >
                <MessageCircle size={16} /> {whatsappSending ? "Enviando..." : whatsappSentOnce ? "Reenviar por WhatsApp" : "Enviar por WhatsApp"}
              </Button>
            )}
            {!signLink?.ticket?.signed_at && (
              <Button
                variant="outline" onClick={shareWhatsappFree}
                disabled={whatsappShareOpening}
                title="Abre o WhatsApp com a mensagem pronta — você confirma o envio por lá"
                data-testid="share-whatsapp-free"
              >
                <MessageCircle size={16} /> {whatsappShareOpening ? "Abrindo..." : "Compartilhar pelo WhatsApp"}
              </Button>
            )}
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={copyLink} data-testid="copy-sign-link"><Copy size={16} /> Copiar link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteOpen} onOpenChange={v => !v && !deleting && setDeleteOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={18} /> Excluir ficha #{deleteOpen?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
              Por favor, verifique e confirme se realmente deseja excluir esta ficha.
            </div>
            <p className="text-sm text-slate-600">
              Esta ação removerá a ficha das consultas normais, mas manterá o registro para auditoria.
            </p>
            <div>
              <Label>Motivo da exclusão *</Label>
              <Textarea rows={3} value={deleteReason} onChange={e => setDeleteReason(e.target.value)} data-testid="delete-reason" />
            </div>
            <div>
              <Label>Após a leitura do texto acima, digite <b>{DELETE_CONFIRM_PHRASE}</b> para prosseguir</Label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={DELETE_CONFIRM_PHRASE}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="delete-confirm-text"
              />
              {deleteConfirmText.length > 0 && !deleteConfirmOk && (
                <p className="text-xs text-red-600 mt-1">Digite exatamente {DELETE_CONFIRM_PHRASE} para prosseguir.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(null)} disabled={deleting}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={!deleteReasonOk || !deleteConfirmOk || deleting}
              data-testid="confirm-delete-ticket"
            >
              <Trash2 size={16} /> Excluir ficha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
